import { sessionInfoResponse } from "~/backend/validate.js";
/** @import { sessionEnterprisePermissionsResponse, sessionOrgPermissionsResponse } from "~/backend/validate.js"; */
import { query, response } from "~/lib/api.js";

const q = query(() => ({ key: ["/v2/session", { include_permissions: true }] }), {
	schema: response(sessionInfoResponse),
});

/** @typedef {"host-only" | "client-only" | "host-client"} SessionType */
/**
 * @typedef {object} Feature
 * @property {"members" | "orgs" | "settings" | "billing" | "api_keys" | "audit_logs"} enterprise
 * @property {"members"
 * 	| "groups"
 * 	| "machines"
 * 	| "machine_connect"
 * 	| "machine_keys"
 * 	| "locations"
 * 	| "relays"
 * 	| "connections"
 * 	| "settings"} org
 */

export const identity = {
	get loading() {
		return q.loading;
	},
	get error() {
		return q.error;
	},
	get initialized() {
		return q.initialized;
	},

	get me() {
		return q.data?.data?.user ?? null;
	},
	get machine() {
		return q.data?.data?.machine ?? null;
	},
	get enterprise() {
		return q.data?.data?.enterprise ?? null;
	},

	/**
	 * @returns {{
	 * 	enterprise?: sessionEnterprisePermissionsResponse;
	 * 	orgs: Record<string, sessionOrgPermissionsResponse>;
	 * }}
	 */
	get permissions() {
		return q.data?.data?.permissions ?? { orgs: {} };
	},

	/** @returns {SessionType} */
	get sessionType() {
		if (this.machine && !this.me) return "host-only";
		if (!this.machine && this.enterprise) return "client-only";
		return "host-client";
	},

	get can() {
		return {
			host: this.sessionType !== "client-only",
			connect: this.sessionType !== "host-only",
			account: this.sessionType !== "host-only",
		};
	},

	/**
	 * @template {string} S
	 * @param {S} scope
	 * @param {S extends "enterprise" ? Feature["enterprise"] : Feature["org"]} feature
	 * @param {"read" | "edit"} [access]
	 */
	permission(scope, feature, access = "read") {
		const permissions = q.data?.data?.permissions;
		const granted =
			scope === "enterprise"
				? permissions?.enterprise.permissions
				: permissions?.orgs[scope]?.permissions;
		const level = granted?.[feature];
		return level === access || level === "edit";
	},

	canAccessEnterprise() {
		return Object.keys(this.permissions.enterprise?.permissions ?? {}).length > 0;
	},

	/** @param {string} orgId */
	canAccessOrg(orgId) {
		return Object.keys(this.permissions.orgs[orgId]?.permissions ?? {}).length > 0;
	},

	fetch: () => q.fetch(),
};
